import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { MouseEvent, useMemo } from 'react';
import './../../styles/quill.snow.css';

interface Props {
  content: string;
}

const youtubeEmbedPattern = /^https?:\/\/(?:www\.)?youtube(?:-nocookie)?\.com\/embed\/([\w-]{11})/;

const playButtonSvg =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 68 48'%3E%3Cpath d='M66.5 7.7a8.6 8.6 0 0 0-6-6C55.2.3 34 .3 34 .3S12.8.3 7.5 1.7a8.6 8.6 0 0 0-6 6C0 13 0 24 0 24s0 11 1.5 16.3a8.6 8.6 0 0 0 6 6C12.8 47.7 34 47.7 34 47.7s21.2 0 26.5-1.4a8.6 8.6 0 0 0 6-6C68 35 68 24 68 24s0-11-1.5-16.3z' fill='%23f00'/%3E%3Cpath d='M45 24 27 14v20' fill='%23fff'/%3E%3C/svg%3E\")";

const HtmlViewerContainer = styled(Box)({
  color: 'black',
  fontFamily: '"Roboto", sans-serif',
  '.ql-video': {
    width: '800px',
    height: '450px',
    borderRadius: '15px',
    '@media (max-width: 800px)': {
      width: '600px',
      height: '340px',
    },
    '@media (max-width: 600px)': {
      width: '400px',
      height: '225px',
    },
    '@media (max-width: 400px)': {
      width: '300px',
      height: '165px',
    },
  },
  '.youtube-thumbnail': {
    display: 'block',
    position: 'relative',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: '68px',
      height: '48px',
      transform: 'translate(-50%, -50%)',
      backgroundImage: playButtonSvg,
    },
  },
  img: {
    maxWidth: '100%',
  },
});

const isPlatformIOS = () => window.cordova?.platformId?.toString().toLowerCase() === 'ios';

// YouTube rejects embeds from the iOS app's app://localhost origin (error 153),
// so on iOS embedded videos are replaced with thumbnails that open YouTube externally
export const replaceYoutubeEmbeds = (content: string) => {
  const document = new DOMParser().parseFromString(content, 'text/html');
  const iframes = document.querySelectorAll('iframe');
  if (iframes.length === 0) {
    return content;
  }

  iframes.forEach((iframe) => {
    const videoId = iframe.getAttribute('src')?.match(youtubeEmbedPattern)?.[1];
    if (!videoId) {
      return;
    }

    const link = document.createElement('a');
    link.className = `${iframe.className} youtube-thumbnail`.trim();
    link.href = `https://www.youtube.com/watch?v=${videoId}`;
    link.style.backgroundImage = `url(https://img.youtube.com/vi/${videoId}/hqdefault.jpg)`;
    iframe.replaceWith(link);
  });

  return document.body.innerHTML;
};

export function HtmlViewer(props: Props) {
  const { content } = props;
  const html = useMemo(() => (isPlatformIOS() ? replaceYoutubeEmbeds(content) : content), [content]);

  const onClick = (event: MouseEvent<HTMLElement>) => {
    const link = (event.target as HTMLElement).closest<HTMLAnchorElement>('a.youtube-thumbnail');
    if (!link) {
      return;
    }

    event.preventDefault();
    window.cordova.InAppBrowser.open(link.href, '_system');
  };

  return <HtmlViewerContainer className="ql-editor" onClick={onClick} dangerouslySetInnerHTML={{ __html: html }} />;
}
